import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ArabicWord, User } from '../types';
import { jwtDecode } from 'jwt-decode';
import { API_GATEWAY_URL, GOOGLE_TOKEN_KEY, DEFAULT_HEADERS, getEarliestWordDate, getCurrentLanguage } from '../constants';
import { fetchWordOfTheDay } from '../services/wordService';
import WordCard from './WordCard';
import SubscriptionSection from './SubscriptionSection';
import LoadingOverlay from './LoadingOverlay';
import Navigation from './Navigation';
import { getPacificDate, formatDate, getTodayInPacific, setToMidnight } from '../utils/dateUtils';

// Pacific timezone suffix for date strings (midnight in PST/PDT)
const PACIFIC_TIMEZONE_SUFFIX = 'T00:00:00-08:00';

const HomePage: React.FC = () => {
  const [word, setWord] = useState<ArabicWord | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isNavigating, setIsNavigating] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [subscribed, setSubscribed] = useState<boolean>(false);
  const [isEmailConfirmed, setIsEmailConfirmed] = useState<boolean>(false);
  const [subscribing, setSubscribing] = useState<boolean>(false);
  const [checkingSubscription, setCheckingSubscription] = useState<boolean>(false);
  const [currentDate, setCurrentDate] = useState<string>('');
  const [isCurrentDay, setIsCurrentDay] = useState<boolean>(true);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const futureDate = urlParams.get('future_date');
    
    // Default date is today in Pacific time
    const todayPacific = getTodayInPacific();
    
    // Only use future_date for testing if available, otherwise use today
    const effectiveDate = futureDate || todayPacific;
    
    setCurrentDate(effectiveDate);
    setIsCurrentDay(!futureDate);
    
    getWordOfTheDay(futureDate || undefined, false);
    checkSession();
  }, []);

  const getWordOfTheDay = async (date?: string, navigating: boolean = false): Promise<void> => {
    try {
      // Set appropriate loading state based on whether we're navigating
      if (navigating) {
        setIsNavigating(true);
      } else {
        setLoading(true);
      }
      
      // Check if we're in future_date testing mode
      const urlParams = new URLSearchParams(window.location.search);
      const futureDate = urlParams.get('future_date');
      
      // Priority: 1. future_date from URL, 2. provided date, 3. today in Pacific
      let dateToFetch = futureDate || date;
      
      // If no date is specified, use today's date in Pacific time
      if (!dateToFetch) {
        dateToFetch = getTodayInPacific();
      }
      
      const wordData = await fetchWordOfTheDay(dateToFetch);
      setWord(wordData);
      setLoading(false);
      setIsNavigating(false);
    } catch (err) {
      setError('Failed to fetch word of the day');
      setLoading(false);
      setIsNavigating(false);
      console.error('Error fetching word:', err);
    }
  };

  const handlePreviousDay = () => {
    // Use currentDate from state instead of word.displayDate, ensuring Pacific timezone
    const currentWordDate = new Date(`${currentDate}${PACIFIC_TIMEZONE_SUFFIX}`);
    const previousDay = new Date(currentWordDate);
    previousDay.setDate(previousDay.getDate() - 1);
    
    // Don't go earlier than the earliest date from constants
    const earliestDate = new Date(getEarliestWordDate());
    if (previousDay < earliestDate) {
      previousDay.setTime(earliestDate.getTime());
    }
    
    const formattedDate = formatDate(previousDay);
    
    // Check if this is today's date (current word) in Pacific time
    const todayPacific = getPacificDate();
    const todayMidnight = setToMidnight(todayPacific);
    
    setIsCurrentDay(previousDay.getTime() === todayMidnight.getTime());
    setCurrentDate(formattedDate);
    
    // Check if we're in future_date testing mode
    const urlParams = new URLSearchParams(window.location.search);
    const isFutureTest = urlParams.has('future_date');
    
    // Only update URL if in future_date testing mode
    if (isFutureTest) {
      const url = new URL(window.location.href);
      url.searchParams.set('future_date', formattedDate);
      window.history.pushState({}, '', url);
    }
    
    getWordOfTheDay(formattedDate, true);
  };

  const handleNextDay = () => {
    // Use currentDate from state instead of word.displayDate, ensuring Pacific timezone
    const currentWordDate = new Date(`${currentDate}${PACIFIC_TIMEZONE_SUFFIX}`);
    const nextDay = new Date(currentWordDate);
    nextDay.setDate(nextDay.getDate() + 1);
    nextDay.setHours(0, 0, 0, 0);
    
    // Get today in Pacific time
    const todayPacific = getPacificDate();
    const todayMidnight = setToMidnight(todayPacific);
    
    // Check if we're in future_date testing mode
    const urlParams = new URLSearchParams(window.location.search);
    const isFutureTest = urlParams.has('future_date');
    
    // Don't go beyond today unless in future testing mode
    if (nextDay > todayMidnight && !isFutureTest) {
      nextDay.setTime(todayMidnight.getTime());
      setIsCurrentDay(true);
    } else {
      setIsCurrentDay(nextDay.getTime() === todayMidnight.getTime());
    }
    
    const formattedDate = formatDate(nextDay);
    setCurrentDate(formattedDate);
    
    // Only update URL if in future_date testing mode
    if (isFutureTest) {
      const url = new URL(window.location.href);
      url.searchParams.set('future_date', formattedDate);
      window.history.pushState({}, '', url);
    }
    
    getWordOfTheDay(formattedDate, true);
  };

  const checkSubscriptionStatus = async (tokenUser: User): Promise<void> => {
    if (!tokenUser) return;

    try {
      setCheckingSubscription(true);
      const response = await axios.get(`${API_GATEWAY_URL}/subscriptions/${tokenUser.Email}?language=${getCurrentLanguage()}`, {
        headers: DEFAULT_HEADERS
      });
      setSubscribed(response?.data?.isSubscribed ?? false);
      setIsEmailConfirmed(response?.data?.isEmailConfirmed ?? false);
    } catch (err) {
      setSubscribed(false);
      console.error('Failed to check subscription status', err);
    } finally {
      setCheckingSubscription(false);
    }
  };

  const checkSession = () => {
    const token = localStorage.getItem(GOOGLE_TOKEN_KEY);
    if (!token) return;
    const decoded = jwtDecode(token) as any;

    const tokenUser = {
      Name: decoded.name ?? '',
      Email: decoded.email ?? '',
      GoogleId: ''
    }
    setUser(tokenUser);

    checkSubscriptionStatus(tokenUser);
  };

  const playAudio = (): void => {
    if (word && word.audioUrl) {
      const audio = new Audio(word.audioUrl);
      audio.play();
    }
  };

  // Calculate if we should disable the previous button
  const isEarliestDate = currentDate ? 
    setToMidnight(new Date(currentDate)).getTime() <= setToMidnight(new Date(getEarliestWordDate())).getTime() : 
    false;

  return (
    <div className="App">
      <header className="App-header">
        <h1>{getCurrentLanguage()} Word of the Day</h1>
        <Navigation />
      </header>
      
      <main className="App-main">
        {loading ? (
          <p>Loading...</p>
        ) : error ? (
          <p className="error">{error}</p>
        ) : word ? (
          <div className="word-container" style={{ position: 'relative' }}>
            <LoadingOverlay isLoading={isNavigating} />
            <WordCard 
              word={word} 
              playAudio={playAudio} 
              onPrevious={handlePreviousDay}
              onNext={handleNextDay}
              isCurrentDay={isCurrentDay}
              disablePrevious={isEarliestDate}
              displayDate={currentDate}
            />
          </div>
        ) : (
          <p>No word available</p>
        )}

        <SubscriptionSection
          user={user}
          setUser={setUser}
          subscribed={subscribed}
          setSubscribed={setSubscribed}
          isEmailConfirmed={isEmailConfirmed}
          setIsEmailConfirmed={setIsEmailConfirmed}
          subscribing={subscribing}
          setSubscribing={setSubscribing}
          checkingSubscription={checkingSubscription}
          error={error}
          setError={setError}
        />
      </main>
      
      <footer className="App-footer">
        <p>&copy; {new Date().getFullYear()} {getCurrentLanguage()} Word of the Day</p>
      </footer>
    </div>
  );
};

export default HomePage;